/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment } from "react"
import { isEmpty } from "lodash"
import dayjs from "dayjs"
import {
  getPeriods,
  days,
  openPeriodTypes,
  OpenPeriodText,
} from "utils/section_helper"
import { useTranslation } from "common/utils/i18n"
import { Section } from "types/models/menu"
import Tippy from "@tippy.js/react"
import { dropdownSubtextStyle } from "./dropdownStyles"

const limitRows = 2
interface SectionPeriodsProps {
  section: Section
}
export const SectionPeriods: FC<SectionPeriodsProps> = ({
  section,
}: SectionPeriodsProps) => {
  const periods = getPeriods(section.openingHours!)
  const openPeriodText = OpenPeriodText()

  const ranges = Object.keys(periods).sort((a, b) => {
    const [startA] = a.split("-")
    const [startB] = b.split("-")

    return days.indexOf(startA) - days.indexOf(startB)
  })

  const renderRange = (range: string) => {
    const [startDay, endDay] = range.split("-")

    return endDay ? `${startDay} - ${endDay}` : startDay
  }

  const renderPeriod = (period: string) => {
    const [startTime, endTime] = period.split("-")

    if (startTime === "00:00" && endTime === "24:00") {
      return openPeriodText[openPeriodTypes.OPEN_ALL_DAY]
    }

    const format = (time: string) => {
      const inFormat = "HH:mm"
      const outFormatWithoutHours = ".mm A"
      const outFormat = `h${outFormatWithoutHours}`
      const m = dayjs(time, inFormat)
      if (m.hour() === 0) {
        return `00${m.format(outFormatWithoutHours)}`
      }
      return m.format(outFormat)
    }

    const start = format(startTime)
    const end = format(endTime)

    return `${start} - ${end}`
  }

  const renderDropdownSubtext = (range: string) => {
    return `${renderRange(range)} : ${periods[range]
      .map(renderPeriod)
      .join(", ")}`
  }

  const textMore = useTranslation("common.more")

  const moreRemaining = ranges.length - limitRows
  const showMore = moreRemaining > 0

  return (
    <Fragment>
      {isEmpty(periods) && (
        <div css={dropdownSubtextStyle}>
          {openPeriodText[openPeriodTypes.CLOSED_ALL_DAY]}
        </div>
      )}
      {!isEmpty(periods) &&
        ranges.slice(0, limitRows).map((range, index) => (
          <Fragment key={range}>
            <div css={dropdownSubtextStyle}>
              {renderDropdownSubtext(range)}{" "}
              {index === limitRows - 1 && showMore && (
                <Tippy
                  content={ranges.slice(limitRows).map(r => (
                    <div key={r}>{renderDropdownSubtext(r)}</div>
                  ))}
                  placement="bottom"
                >
                  <span
                    css={css`
                      font-weight: 500;
                    `}
                  >
                    + {moreRemaining} {textMore}
                  </span>
                </Tippy>
              )}
            </div>
          </Fragment>
        ))}
    </Fragment>
  )
}
